import React, { useEffect, useState } from "react"
import {
  SignInModal,
  NotificationIndicator,
  UserMenu,
  ThemeSwitcher,
  Button,
  ModerationIndicator,
  LocaleSwitcher,
  WelcomeModal,
  EventBanner,
  SystemStatus,
  ScrollToTop,
  BrandMark,
  Icon,
} from "@fider/components"
import { useFider } from "@fider/hooks"
import { BRAND_WORDMARK } from "@fider/services/brand"
import { BRAND_INTRO_EVENT } from "@fider/services/brandIntro"
import { classSet } from "@fider/services"
import IconSearch from "@fider/assets/images/heroicons-search.svg"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"
import "./Header.scss"

interface HeaderProps {
  hasInert?: boolean
}

// Tùy biến KHCN: header theo bản thiết kế. Trái: logo bóng đèn + wordmark + tagline.
// Giữa: điều hướng có icon (Trang chủ / Ý tưởng / Lộ trình / Thông báo). Phải: tìm kiếm, sự kiện,
// trạng thái hệ thống (staff), cụm ngôn ngữ / giao diện / thông báo, rồi ảnh đại diện hoặc nút Đăng nhập.

// Icon điều hướng vẽ inline (nét heroicons) — chỉ dùng trong header
const NavGlyph = ({ d }: { d: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d={d} />
  </svg>
)

const GLYPH = {
  home: "M3 11.5 12 4l9 7.5M5 10v10h5v-6h4v6h5V10",
  idea: "M9.7 18h4.6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.4 1.3 2.2h4.6c.2-.8.6-1.6 1.3-2.2A6 6 0 0 0 12 3z",
  roadmap: "M4 20l5-2 6 2 5-2V6l-5 2-6-2-5 2v14M9 4v14M15 6v14",
  bell: "M15 17H9m6 0h4l-1.4-1.4A2 2 0 0 1 17 14.2V11a5 5 0 0 0-10 0v3.2a2 2 0 0 1-.6 1.4L5 17h4m6 0a3 3 0 0 1-6 0",
  user: "M20 21a8 8 0 0 0-16 0M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
}

export const Header = (props: HeaderProps) => {
  const fider = useFider()
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)

  // Tên trên header: wordmark "2026" cho tới khi ấn ký "Hà Tĩnh" trong hero bay vào (xem HeroBanner),
  // sau đó là tên diễn đàn cho tới khi tải lại trang.
  const [forumName, setForumName] = useState(false)
  const [landing, setLanding] = useState(false)

  useEffect(() => {
    let timer = 0
    const onLand = () => {
      setForumName(true)
      setLanding(true)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setLanding(false), 1600)
    }
    window.addEventListener(BRAND_INTRO_EVENT, onLand)
    return () => {
      window.removeEventListener(BRAND_INTRO_EVENT, onLand)
      window.clearTimeout(timer)
    }
  }, [])

  const pathname = typeof window !== "undefined" ? window.location.pathname : "/"

  // Nút tìm kiếm trên header: cuộn tới ô tìm kiếm trong hero (trang chủ), nơi khác thì về trang chủ
  const focusHeroSearch = () => {
    const input = document.getElementById("kh-hero-search") as HTMLInputElement | null
    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" })
      input.focus()
    } else {
      window.location.href = "/#danh-sach"
    }
  }

  const navLink = (href: string, active: boolean, glyph: string, label: React.ReactNode) => (
    <a href={href} className={`c-header__nav-link ${active ? "c-header__nav-link--active" : ""}`}>
      <NavGlyph d={glyph} />
      <span>{label}</span>
    </a>
  )

  const searchLabel = i18n._({ id: "khcn.header.search", message: "Search" })

  return (
    <div id="c-header" {...(props.hasInert && { inert: "true" })}>
      <SignInModal isOpen={isSignInModalOpen} onClose={() => setIsSignInModalOpen(false)} />
      <WelcomeModal />
      <ScrollToTop />
      <div className="container c-header__inner">
        <a
          href="/"
          className={classSet({ "c-header__brand": true, "c-header__brand--forum": forumName, "c-header__brand--landing": landing })}
          aria-label={fider.session.tenant.name}
        >
          <BrandMark size={44} />
          <span className="c-header__brand-text">
            {forumName ? (
              <span className="c-header__wordmark c-header__wordmark--forum">
                <span className="c-header__wordmark-line">{i18n._({ id: "khcn.header.name1", message: "Science & Technology" })}</span>
                <span className="c-header__wordmark-line">{i18n._({ id: "khcn.header.name2", message: "and Innovation Forum" })}</span>
              </span>
            ) : (
              <span className="c-header__wordmark c-header__wordmark--year">
                {BRAND_WORDMARK}
                <span className="c-header__wordmark-spark" aria-hidden="true" />
              </span>
            )}
            <span className="c-header__tagline">{i18n._({ id: "khcn.header.tagline", message: "Ideas today – Hà Tĩnh tomorrow" })}</span>
          </span>
        </a>

        <nav className="c-header__nav" aria-label={i18n._({ id: "header.nav.label", message: "Main navigation" })}>
          {navLink("/", pathname === "/", GLYPH.home, <Trans id="khcn.header.nav.home">Home</Trans>)}
          {navLink("/#danh-sach", false, GLYPH.idea, <Trans id="header.nav.feedback">All Feedback</Trans>)}
          {navLink("/roadmap", pathname === "/roadmap", GLYPH.roadmap, <Trans id="header.nav.roadmap">Roadmap</Trans>)}
          {fider.session.isAuthenticated &&
            navLink("/notifications", pathname === "/notifications", GLYPH.bell, <Trans id="khcn.header.nav.notifications">Notifications</Trans>)}
        </nav>

        {fider.session.isAuthenticated && (
          <div className="c-header__moderation">
            <ModerationIndicator />
          </div>
        )}

        <div className="c-header__actions">
          <button type="button" className="c-header__search" onClick={focusHeroSearch} aria-label={searchLabel} title={searchLabel}>
            <Icon sprite={IconSearch} />
          </button>
          <EventBanner />
          {fider.session.isAuthenticated && <SystemStatus />}
          <div className="c-header__cluster">
            <LocaleSwitcher />
            <ThemeSwitcher />
            {fider.session.isAuthenticated && <NotificationIndicator />}
          </div>
          {fider.session.isAuthenticated ? (
            <UserMenu />
          ) : (
            <Button variant="primary" size="small" className="c-header__signin" onClick={() => setIsSignInModalOpen(true)}>
              <NavGlyph d={GLYPH.user} />
              <Trans id="action.signin">Sign in</Trans>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
